import * as React from "react";
import Emojify from "~/components/shared/Emojify";
import { shortCommit } from "~/lib/commits";
import classNames from "classnames";
import { jobTime } from "~/lib/jobs";
import Duration from "~/components/shared/Duration";
import Icon from "~/components/shared/Icon";
import PipelineStateIcon from "~/components/shared/PipelineStateIcon";
import { elementIdFor } from "~/components/job/Job";
import { TriggerJob } from "~/components/build/Show/lib/types";
import { BuildStates, isTerminalBuildState } from "~/constants/BuildStates";
import { MissingDependenciesNode } from "~/components/build/Show/components/MissingDependenciesNode";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "~/components/shared/HoverCard";
import { MissingStepDependencies } from "~/components/job/Job/MissingStepDependencies";

type Props = {
  className?: string;
  job: TriggerJob;
  isRetry: boolean | null | undefined;
  build: {
    state: BuildStates;
  };
};

export default class TriggerJobSummary extends React.PureComponent<Props> {
  render() {
    let state =
      this.props.job.state === "finished" ||
      this.props.job.state === "timed_out" ||
      this.props.job.state === "canceled" ||
      this.props.job.state === "expired"
        ? this.props.job.passed
          ? "passed"
          : "failed"
        : this.props.job.state;

    // Canceling and timing out are still running, so let's show them as such.
    if (state === "canceling" || state === "timing_out") {
      state = "running";
    }

    const triggerFailed = !!this.props.job.triggerFailedReason;

    let contentsNode = (
      <div
        className={classNames(
          "build-details-pipeline-job__header hover hover-bg-silver z-[2] flex items-center gap-x-2.5 bg-white px-3 py-3.5 antialiased",
          {
            "!top-0 !rounded-none !border-x-0 !border-t-gray-400 !py-2.5 px-2.5":
              Features.BuildSidebar,
          },
        )}
      >
        <div className="flex min-w-0 flex-auto items-center gap-x-1.5">
          <span className="build-details-pipeline-icon">
            <PipelineStateIcon job={this.props.job} />
          </span>

          <span className="job-name max-w-full flex-none truncate">
            <Emojify text={this.props.job.name || "Trigger Build"} />
          </span>

          <span className="dark-gray max-w-full truncate">
            {this.props.isRetry ? "Rebuild" : "Triggered build"}
            {" of "}
            <code className="monospace">{this.props.job.triggerBranch}</code>
            {" \x2F "}
            <code className="monospace" title={this.props.job.triggerCommit}>
              {shortCommit(this.props.job.triggerCommit, this.props.job.triggerCommitShortLength)}
            </code>
            {" on "}
            {this.nameNode()}
          </span>
        </div>

        {triggerFailed ? (
          this.errorMessageNode()
        ) : (
          <div className="flex flex-none items-center">
            <MissingDependenciesNode job={this.props.job} build={this.props.build} />

            {this.durationNode()}
            {this.deletedNode()}

            {this.props.job.triggeredBuild && (
              <div className="dark-gray flex-none pl-2">
                <Icon icon="chevron-right" style={{ height: 12, width: 12 }} />
              </div>
            )}
          </div>
        )}
      </div>
    );

    const missingDependencies = (
      <MissingStepDependencies
        group={false}
        stepUuid={this.props.job.stepUuid}
        variant={isTerminalBuildState(this.props.build.state) ? "error" : "warning"}
      />
    );

    const classes = classNames(
      "build-details-pipeline-job relative color-inherit focus-color-inherit no-underline hover-color-inherit block",
      {
        [`build-details-pipeline-job-state-${state}`]: true,
        "!mb-0": Features.BuildSidebar,
      },
      this.props.className,
    );

    if (this.props.job.triggeredBuild && !this.props.job.triggeredBuild.destroyedAt) {
      contentsNode = (
        <a href={this.props.job.triggeredBuild.url} className={classes} id={this.elementId()}>
          {contentsNode}
          {missingDependencies}
        </a>
      );
    } else {
      contentsNode = (
        <div className={classes} id={this.elementId()}>
          {contentsNode}
          {missingDependencies}
        </div>
      );
    }

    return contentsNode;
  }

  nameNode() {
    // triggerProject might be soft-deleted, in which case it has a destroyedAt.
    // If triggerProject has been hard-deleted, it will be null/undefined/nada.
    // However triggerProjectSlug etc remain stored on the job, so those remain.
    if (this.props.job.triggerProject) {
      return <Emojify text={this.props.job.triggerProject.name} />;
    }
    if (this.props.job.triggerProjectSlug) {
      return <code className="monospace">{this.props.job.triggerProjectSlug}</code>;
    }
    return "unknown pipeline";
  }

  durationNode() {
    const buildNumber = this.props.job.triggeredBuild
      ? `#${this.props.job.triggeredBuild.number}`
      : "";

    if (this.props.job.triggerAsync) {
      return (
        <div className="xs-flex-wrap sm-flex-none dark-gray pl-2">
          Build {buildNumber} (asynchronous)
        </div>
      );
    }

    if (this.props.job.startedAt) {
      const runTense = this.props.job.state === "running" ? "running for" : "ran in";

      const duration = jobTime(this.props.job);

      return (
        <div className="xs-flex-wrap sm-flex-none dark-gray pl-2">
          <span>
            Build {buildNumber} {runTense} <Duration.Short {...duration} />
          </span>
        </div>
      );
    }
  }

  deletedNode() {
    const job = this.props.job;
    return (
      (job.triggeredBuildDeleted === true ||
        (job.triggeredBuild && job.triggeredBuild.destroyedAt)) && (
        <div className="red ml-1 flex-none">(Deleted)</div>
      )
    );
  }

  errorMessageNode() {
    const triggerDenied =
      window.Features.Rules && this.props.job.triggerFailedReason?.startsWith("Trigger denied:");
    return (
      this.props.job.triggerFailedReason && (
        <HoverCard>
          <HoverCardTrigger>
            <div className="red ml-1 max-w-3xl flex-none">
              <Icon icon="heroicons/solid/exclamation-triangle" className="mr-1 h-4 w-4" />
              {triggerDenied ? "Trigger denied" : "Trigger failed"}
            </div>
          </HoverCardTrigger>

          <HoverCardContent size="max-lg">
            {triggerDenied ? (
              <div>
                <p>{this.props.job.triggerFailedReason}</p>
                <br />
                {this.props.job.triggerFailedReason ===
                  "Trigger denied: Builds can only be triggered between pipelines in the same cluster" && (
                  <div>
                    <strong>Tip:</strong> If you are trying to trigger a build between pipelines in
                    different clusters, you can create a{" "}
                    <a href="/docs/pipelines/security/clusters/rules">
                      <code>pipeline.trigger_build.pipeline</code> rule
                    </a>{" "}
                    to allow triggering a pipeline in another cluster.
                  </div>
                )}
              </div>
            ) : (
              <div>{this.props.job.triggerFailedReason}</div>
            )}
          </HoverCardContent>
        </HoverCard>
      )
    );
  }

  elementId() {
    return elementIdFor(this.props.job.id);
  }
}
